import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import Seo from '../../components/Seo';
import type { LocaleCode } from '../../config/i18n';
import { freeTasteSamples, type FreeTasteSample } from '../freeTasteSamples';

/**
 * Landing-only Free Taste demo. No auth, no Instagram OAuth, no scheduling,
 * no panel access — sales-side quality preview only.
 */
export default function FreeTastePage() {
  const { t, i18n } = useTranslation();
  const locale: LocaleCode = i18n.language === 'en' ? 'en' : 'tr';
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [samples, setSamples] = useState<FreeTasteSample[] | null>(null);
  const [error, setError] = useState('');

  function generate(e: React.FormEvent) {
    e.preventDefault();
    const clean = username.replace(/^@/, '').trim();
    if (!clean) {
      setError(t('freeTaste.usernameRequired'));
      return;
    }
    setError('');
    setBusy(true);
    setSamples(null);
    window.setTimeout(() => {
      setSamples(freeTasteSamples(clean));
      setBusy(false);
    }, 1400);
  }

  return (
    <div className="mx-auto max-w-[720px] px-6 py-16">
      <Seo
        title={t('freeTaste.seoTitle')}
        description={t('freeTaste.seoDescription')}
        locale={locale}
        canonicalPath="/free-taste"
      />
      <div className="mb-3 text-[12.5px] font-semibold text-lc-orange">{t('freeTaste.eyebrow')}</div>
      <h1 className="mb-3 text-[34px] font-extrabold tracking-tight sm:text-[40px]">{t('freeTaste.title')}</h1>
      <p className="mb-8 text-[15.5px] leading-relaxed text-lc-muted">{t('freeTaste.subtitle')}</p>

      <ul className="mb-8 space-y-2 text-[13.5px] text-lc-soft">
        <li>• {t('freeTaste.bulletNoCard')}</li>
        <li>• {t('freeTaste.bulletNoConnect')}</li>
        <li>• {t('freeTaste.bulletNoPublish')}</li>
      </ul>

      <form onSubmit={generate} className="mb-10 rounded-[14px] border border-lc-border bg-lc-card p-6">
        <label htmlFor="free-taste-user" className="mb-2 block text-sm font-semibold text-lc-text">
          {t('freeTaste.usernameLabel')}
        </label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lc-faint">@</span>
            <input
              id="free-taste-user"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('freeTaste.usernamePlaceholder')}
              className="w-full rounded-lg border border-lc-borderStrong bg-lc-surface py-3 pl-8 pr-3 text-sm text-lc-text outline-none focus:border-lc-pink"
              autoComplete="off"
              disabled={busy}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-lc-pink px-5 py-3 text-sm font-bold text-lc-ink hover:bg-lc-pinkHover disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4" />
            {busy ? t('freeTaste.generating') : t('freeTaste.generate')}
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
      </form>

      {busy && (
        <div className="mb-10 rounded-[14px] border border-lc-border bg-lc-surface p-10 text-center text-lc-muted">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-[3px] border-lc-borderStrong border-t-lc-pink" />
          {t('freeTaste.generatingHint')}
        </div>
      )}

      {samples && !busy && (
        <div className="mb-12 space-y-5">
          <h2 className="text-lg font-bold text-lc-text">{t('freeTaste.resultsTitle')}</h2>
          {samples.map((s, i) => (
            <article key={i} className="overflow-hidden rounded-[14px] border border-lc-border bg-lc-card">
              <div
                className="flex h-40 items-end p-4 text-sm font-semibold text-white/90"
                style={{
                  background: `linear-gradient(135deg, hsl(${s.visualHue} 42% 28%), hsl(${(s.visualHue + 40) % 360} 50% 18%))`,
                }}
              >
                {s.visualLabel}
              </div>
              <div className="p-5">
                <p className="whitespace-pre-wrap text-[14.5px] leading-relaxed text-lc-soft">{s.caption}</p>
                <p className="mt-3 text-xs text-lc-faint">
                  {s.hashtags.map((tag) => `#${tag}`).join(' ')}
                </p>
              </div>
            </article>
          ))}
          <div className="rounded-[14px] border border-lc-pink/40 bg-lc-surface px-6 py-8 text-center">
            <p className="mb-4 text-[15px] text-lc-muted">{t('freeTaste.upsell')}</p>
            <Link to="/register" className="btn-lc inline-flex px-6 py-3 text-sm">
              {t('freeTaste.upsellCta')}
            </Link>
          </div>
        </div>
      )}

      <p className="text-center text-[13px] text-lc-faint">
        <Link to="/#pricing" className="text-lc-muted hover:text-lc-pink">
          {t('freeTaste.backToPricing')}
        </Link>
      </p>
    </div>
  );
}
